import _ from 'lodash';
import api from 'src/config/api';
import { Pagination } from 'src/types/services';
import {
  PolicyTypesResponse,
  ResponseDetailPolicyTypes,
  ResponseRequestTimeOff,
  Vacations,
  ApprovalStepsType,
  VacationsNotificationsResponse,
  ResponseBalanceReport,
  AdjustAvailableTimeParams,
  BalanceConfomationResponse,
  VacationStatus,
  TimeOffRequestedDaysResponse,
  ResponseRequestCalendar,
  PoliciesResponse,
  Role,
  VacationsFilterValues,
  ExtraFieldsReport,
  DateToRequest,
  VacationsPaginated,
} from 'src/types/vacations';
import { UserListResponse } from 'src/types/user';
import { formatISO } from 'date-fns';
import { MeResponse } from 'src/types/login';

const formatVacationsFilters = (filters: VacationsFilterValues) => ({
  policyTypeIds:
    filters?.policyTypeIds &&
    Object.keys(_.pickBy(filters.policyTypeIds)).toString(),
  policyIds:
    filters?.policyIds && Object.keys(_.pickBy(filters.policyIds)).toString(),
  userIds: filters?.users.map(u => u.id).toString(),
  states: filters?.states && Object.keys(_.pickBy(filters.states)).toString(),
  fromDate:
    filters?.fromDate &&
    formatISO(new Date(filters.fromDate), { representation: 'date' }), // can't import localDateToSimpleDateString from utils because of circular dependency
  toDate:
    filters?.toDate &&
    formatISO(new Date(filters.toDate), { representation: 'date' }),
  onlyPeopleWithRequests: filters?.onlyPeopleWithRequests,
  order: filters?.order,
  orderBy: filters?.orderBy,
});

export const getPolicyTypesByUserId = (userId: number) =>
  api.get<PolicyTypesResponse>('/vacations/policy-types', {
    params: {
      userId,
    },
  });

export const getPolicyTypes = () =>
  api.get<PolicyTypesResponse>('/vacations/policy-types');

export const getPolicies = (policyTypes: number[]) =>
  api.get<PoliciesResponse>('/vacations/policies', {
    params: { policyTypeIds: policyTypes.toString(), page: 1, limit: 300 },
  });

export const getVacationsRequestCollaborator = (policyTypeId, page, limit) =>
  api.get<Pagination<Vacations>>('/vacations/requests', {
    params: {
      policyTypeId,
      role: 'COLLABORATOR',
      page,
      limit,
    },
  });

export const getVacationsRequestManager = (
  params: VacationsFilterValues,
  page,
  limit,
) =>
  api.get<Pagination<Vacations>>('/vacations/requests', {
    params: {
      ...formatVacationsFilters(params),
      role: 'MANAGER',
      page,
      limit,
    },
  });

export const createVacationRequest = ({
  policyTypeId,
  from,
  to,
  description,
  attachments,
}) =>
  api.post<ResponseRequestTimeOff>('/vacations/requests', {
    policyTypeId,
    from,
    to,
    description,
    attachments,
  });

export const editFilesInRequest = ({ id, attachments }) =>
  api.put<ResponseRequestTimeOff>(`/vacations/requests/${id}/attachments`, {
    attachments,
  });

export const approveOrRejectRequest = ({
  requestId,
  stepId,
  state,
  resolutionReason,
}) =>
  api.put(`/vacations/requests/${requestId}/approval-steps/${stepId}/state`, {
    state,
    resolutionReason: resolutionReason || undefined,
  });

export const getVacationsDetail = id =>
  api.get<ResponseRequestTimeOff>(`/vacations/requests/${id}`);

export const getPolicyTypeDetail = (
  policyTypeId,
  userIdCollaborator = undefined,
) =>
  api.get<ResponseDetailPolicyTypes>(
    `/vacations/policy-types/${policyTypeId}`,
    {
      params: {
        userId: userIdCollaborator,
      },
    },
  );

export const getApprovalStep = id =>
  api.get<ApprovalStepsType>(`/vacations/requests/${id}/approval-steps`);

export const getApprovalStepApprovers = ({
  vacationId,
  stepId,
  page,
  limit,
  orderBy = 'NAME',
  order = 'ASC',
}) =>
  api.get<UserListResponse>(
    `/vacations/requests/${vacationId}/approval-steps/${stepId}/potential-approvers`,
    {
      params: {
        page,
        limit,
        orderBy: orderBy,
        order: order,
      },
    },
  );

// Notifications
export const getVacationsNotifications = async () => {
  const [response1, response2] = await Promise.all([
    api.get<VacationsNotificationsResponse>('red-bubbles/vacations'),
    api.get<VacationsPaginated>('/vacations/requests', {
      params: {
        role: Role.MANAGER,
        states: VacationStatus.PENDING,
        limit: 1,
      },
    }),
  ]);

  const combinedData = {
    ...response1.data,
    vacationRequestsAsManagerNotifications: response2.data?.count ?? 0,
  };

  return combinedData;
};

export const clearVacationsNotifications = body =>
  api.post('red-bubbles/vacations/clear', body);

export const getBalanceReport = (
  params: VacationsFilterValues,
  page,
  limit,
  orderBy = 'NAME',
  order = 'ASC',
) =>
  api.get<ResponseBalanceReport>('vacations/balances', {
    params: {
      ...formatVacationsFilters(params),
      page,
      limit,
      orderBy,
      order,
    },
  });

export const getUserListSubordinatesForVacations = (
  userId,
  search,
  page,
  limit,
) =>
  api.get(`/organization-charts/${userId}/subordinates`, {
    params: {
      search,
      page,
      limit,
    },
  });

export const adjustAvailableTime = ({
  policyTypeId,
  userId,
  operation,
  amount,
  observations,
}: AdjustAvailableTimeParams) =>
  api.post(
    `vacations/policy-types/${policyTypeId}/balances/manual-correction`,
    {
      userId,
      operation,
      amount,
      observations,
    },
  );

export const getIssuerRequestsList = (search, page, limit, filterSelf) =>
  api.get('/vacations/issuers', {
    params: {
      search: search || undefined,
      page,
      limit,
      filterSelf,
    },
  });

export const getBalanceConformationOfPolicyTypes = (
  policyTypeId: number,
  page: number,
  limit: number,
  userIdCollaborator: number = undefined,
) =>
  api.get<BalanceConfomationResponse>(
    `vacations/policy-types/${policyTypeId}/cycles`,
    {
      params: {
        page,
        limit,
        userId: userIdCollaborator,
      },
    },
  );

export const downloadReportBalances = (
  filters: VacationsFilterValues,
  fileType: string,
  extraFields: ExtraFieldsReport,
) =>
  api.get('vacations/balances/report', {
    responseType: 'blob',
    params: {
      fileType,
      ...formatVacationsFilters(filters),
      ...extraFields,
    },
  });

export const downloadReportRequests = (
  filters: VacationsFilterValues,
  fileType: string,
  extraFields: ExtraFieldsReport,
) =>
  api.get('vacations/requests/report', {
    responseType: 'blob',
    params: {
      fileType,
      ...formatVacationsFilters(filters),
      role: Role.MANAGER,
      ...extraFields,
    },
  });

export const cancelRequest = (requestId, resolutionReason) =>
  api.put(`vacations/requests/${requestId}/state`, {
    state: VacationStatus.CANCELLED,
    resolutionReason: resolutionReason || undefined,
  });

export const getRequestedDays = (
  policyTypeId: number,
  from: DateToRequest,
  to: DateToRequest,
) =>
  api.post<TimeOffRequestedDaysResponse>('vacations/requests/preview', {
    policyTypeId,
    from,
    to,
  });

export const getRequestToCalendar = (
  page: number,
  limit: number,
  month: string,
  filters: VacationsFilterValues,
) =>
  api.get<ResponseRequestCalendar>('/vacations/calendar', {
    params: {
      ...formatVacationsFilters(filters),
      page,
      limit,
      month,
    },
  });

export const massiveApproval = (ids, state) =>
  api.put(`vacations/requests/approval-steps/state`, {
    state,
    ids,
  });

export const sendReminder = requestId =>
  api.post(`vacations/requests/${requestId}/remind-approval-users`);

export const updateRequestByEmail = (otc = '', state = '') =>
  api.put<MeResponse>('/time-off/request-approval-step-state', {
    otc,
    state,
  });
